// User constants (stub for parents)

export const FAVORITE_COLORS = [
  { id: "red", name: "あか", color: "#FF0000" },
  { id: "blue", name: "あお", color: "#0000FF" },
  { id: "green", name: "みどり", color: "#00FF00" },
  { id: "yellow", name: "きいろ", color: "#FFFF00" },
  { id: "pink", name: "ピンク", color: "#FFC0CB" },
  { id: "purple", name: "むらさき", color: "#800080" },
  { id: "orange", name: "オレンジ", color: "#FFA500" },
  { id: "black", name: "くろ", color: "#000000" },
  { id: "white", name: "しろ", color: "#FFFFFF" },
];

export const FAVORITE_THINGS = [
  "sports",
  "music",
  "games",
  "reading",
  "art",
  "animals",
  "nature",
  "science",
  "cooking",
  "travel",
];

// Color palette for AI generation
export type BaseColorKey = "red" | "blue" | "green" | "yellow" | "white";

export const COLOR_PALETTE: Array<{
  key: BaseColorKey;
  labelKey: string;
  hex: string;
}> = [
  { key: "red", labelKey: "MSG_COLOR_RED", hex: "#EF4444" },
  { key: "blue", labelKey: "MSG_COLOR_BLUE", hex: "#3B82F6" },
  { key: "yellow", labelKey: "MSG_COLOR_YELLOW", hex: "#EAB308" },
  { key: "green", labelKey: "MSG_COLOR_GREEN", hex: "#22C55E" },
  { key: "white", labelKey: "MSG_COLOR_WHITE", hex: "#FFFFFF" },
];

/** 色相バリエーションのオフセット（赤・青・黄・緑用） */
const HUE_OFFSETS = [-20, -10, 0, 10, 20];

const hexToHsl = (hex: string): [number, number, number] => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  } else if (max === g) {
    h = ((b - r) / d + 2) * 60;
  } else {
    h = ((r - g) / d + 4) * 60;
  }
  return [h, s, l];
};

const hslToHex = (h: number, s: number, l: number): string => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    [r, g, b] = [c, x, 0];
  } else if (h < 120) {
    [r, g, b] = [x, c, 0];
  } else if (h < 180) {
    [r, g, b] = [0, c, x];
  } else if (h < 240) {
    [r, g, b] = [0, x, c];
  } else if (h < 300) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const generateColorVariations = (baseKey: BaseColorKey): string[] => {
  const baseColor = COLOR_PALETTE.find((c) => c.key === baseKey);
  if (!baseColor) return [];

  // 白の場合はグレースケール（#FFFFFF → #737373 の等間隔5段）
  if (baseKey === "white") {
    return Array.from({ length: 5 }, (_, i) => {
      const v = Math.round(0xff + ((0x73 - 0xff) * i) / 4);
      const hex = v.toString(16).padStart(2, "0");
      return `#${hex}${hex}${hex}`;
    });
  }

  // その他の色は色相バリエーション
  const [h, s, l] = hexToHsl(baseColor.hex);
  return HUE_OFFSETS.map((offset) => {
    const newHue = (h + offset + 360) % 360;
    return hslToHex(newHue, s, l);
  });
};

// Profile field options
export const HAIRSTYLE_OPTIONS = [
  { id: "short", name: "みじかい" },
  { id: "medium", name: "ふつう" },
  { id: "long", name: "ながい" },
];

export const BODY_TYPE_OPTIONS = [
  { id: "slim", name: "ほそい" },
  { id: "normal", name: "ふつう" },
  { id: "large", name: "おおきい" },
];

export const FACE_SHAPE_OPTIONS = [
  { id: "round", name: "まる" },
  { id: "oval", name: "たまご" },
  { id: "square", name: "しかく" },
];
